import { forwardRef, useEffect, useId, useRef, useState, type ButtonHTMLAttributes } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

const Bubble = styled.div`
  position: fixed;
  z-index: 2000;
  max-width: min(240px, calc(100vw - 16px));
  padding: 7px 10px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.color.surfaceChrome};
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: 12px;
  line-height: 1.4;
  box-shadow: 0 4px 16px #0003;
  text-align: center;
  overflow-wrap: anywhere;
`;

/** Fragment + portal keeps button layout, ref, and accessible name unchanged. */
export const TooltipButton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement>>(
  function TooltipButton(
    { title, children, onMouseEnter, onMouseLeave, onFocus, onBlur, onClick, ...props },
    forwardedRef,
  ) {
    const id = useId();
    const button = useRef<HTMLButtonElement | null>(null);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [position, setPosition] = useState<{ left: number; top: number; above: boolean } | null>(
      null,
    );
    const text = title ?? props['aria-label'];
    function cancelTimer() {
      if (timer.current) clearTimeout(timer.current);
    }
    function close() {
      cancelTimer();
      setPosition(null);
    }
    function show() {
      cancelTimer();
      const rect = button.current?.getBoundingClientRect();
      if (!rect || !text || props.disabled) return;
      const half = Math.min(120, (window.innerWidth - 16) / 2);
      setPosition({
        left: Math.max(
          half + 8,
          Math.min(window.innerWidth - half - 8, rect.left + rect.width / 2),
        ),
        top: rect.top > 70 ? rect.top - 8 : rect.bottom + 8,
        above: rect.top > 70,
      });
    }
    useEffect(
      () => () => {
        if (timer.current) clearTimeout(timer.current);
      },
      [],
    );
    useEffect(() => {
      if (!position) return;
      const dismiss = () => setPosition(null);
      const escape = (event: KeyboardEvent) => {
        if (event.key === 'Escape') dismiss();
      };
      window.addEventListener('keydown', escape);
      window.addEventListener('scroll', dismiss, true);
      window.addEventListener('resize', dismiss);
      return () => {
        window.removeEventListener('keydown', escape);
        window.removeEventListener('scroll', dismiss, true);
        window.removeEventListener('resize', dismiss);
      };
    }, [position]);
    return (
      <>
        <button
          {...props}
          ref={(node) => {
            button.current = node;
            if (typeof forwardedRef === 'function') forwardedRef(node);
            else if (forwardedRef) forwardedRef.current = node;
          }}
          aria-describedby={
            [props['aria-describedby'], position ? id : null].filter(Boolean).join(' ') || undefined
          }
          onMouseEnter={(event) => {
            onMouseEnter?.(event);
            cancelTimer();
            timer.current = setTimeout(show, 400);
          }}
          onMouseLeave={(event) => {
            onMouseLeave?.(event);
            cancelTimer();
            if (document.activeElement !== button.current) timer.current = setTimeout(close, 150);
          }}
          onFocus={(event) => {
            onFocus?.(event);
            show();
          }}
          onBlur={(event) => {
            onBlur?.(event);
            close();
          }}
          onClick={(event) => {
            close();
            onClick?.(event);
          }}
        >
          {children}
        </button>
        {position &&
          text &&
          createPortal(
            <Bubble
              id={id}
              role="tooltip"
              style={{
                left: position.left,
                top: position.top,
                transform: `translate(-50%, ${position.above ? '-100%' : '0'})`,
              }}
              onMouseEnter={cancelTimer}
              onMouseLeave={close}
            >
              {text}
            </Bubble>,
            document.body,
          )}
      </>
    );
  },
);
